import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import gql from 'graphql-tag';
import { Button, Input, Select, DatePicker, Checkbox, Progress } from 'antd';
import { useLayer, Arrow } from 'react-laag';
import { motion, AnimatePresence } from 'motion/react';
import EmployeeDetailsModalContainer from './EmployeeDetailsModalContainer';
import { ViewUpdateButtonRenderer, createViewUpdateButtonCell } from './ViewUpdateButtonCell';
import dayjs from 'dayjs';
import {
  DataEditor,
  GridCellKind,
  getMiddleCenterBias
} from "@glideapps/glide-data-grid";
import "@glideapps/glide-data-grid/dist/index.css";
import { downloadFromS3Signed } from 'src/_shared/services/utils';
import { UserProfileCell, TagsCell } from "@glideapps/glide-data-grid-cells";

const nameToColor = (name) => {
    if (!name || name.trim().length === 0) return "#4776E6";
    const colors = [
      "#0062cc", // Deep Blue
      "#6f42c1", // Purple
      "#e83e8c", // Pink
      "#dc3545", // Red
      "#fd7e14", // Orange
      "#28a745", // Green
      "#20c997", // Teal
      "#17a2b8", // Cyan
      "#4361ee", // Indigo
      "#7209b7", // Violet
      "#d90429", // Crimson
      "#118ab2", // Ocean Blue
      "#073b4c", // Dark Teal
      "#3a0ca3", // Deep Purple
      "#4cc9f0", // Bright Blue
      "#4895ef", // Sky Blue
      "#560bad", // Grape
      "#006466", // Dark Cyan
      "#1a759f", // Blue Green
      "#184e77"  // Navy Blue
    ];
    
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const index = Math.abs(hash) % colors.length;
    
    return colors[index];
};

const FilterMenu = ({ column, filterModel, initialFilterData, handleApplyFilter, handleCancelFilter, multiSelectOptions }) => {
    const menuRef = useRef(null);
    const arrayFilters = ["role", "departmentId", "userGroupId", "department"];
    const arrayLabelsMap = {
        role: "Choose Role(s)",
        departmentId: "Choose Department(s)",
        department: "Choose Department(s)",
        userGroupId: "Choose User Group(s)"
    };
    const [operation, setOperation] = useState(
        arrayFilters.includes(column?.id)
            ? "eq"
            : initialFilterData
                ? Object.keys(initialFilterData)[0]
                : column?.id === 'active'
                    ? "eq"
                    : null
    );
    const [filterValue, setFilterValue] = useState(
        arrayFilters.includes(column?.id)
            ? initialFilterData
                ? initialFilterData.map(f => f.eq)
                : []
            : initialFilterData
                ? Object.values(initialFilterData)[0]
                : null
    );
    const [isCaseSensitive, setIsCaseSensitive] = useState(initialFilterData?.isCaseSensitive || true);
    const toISO   = date => (date ? date.toISOString() : null);
    const fromISO = string => (string ? dayjs(string) : null);
    const [singleDate, setSingleDate] = useState(
        column?.dataType === 'date' && !initialFilterData?.between
            ? fromISO(filterValue)
            : null
    );
    const [dateRange, setDateRange] = useState(
        column?.dataType === 'date' && initialFilterData?.between 
            ? [fromISO(initialFilterData.between[0]), fromISO(initialFilterData.between[1])]
            : null
    );
    const [errorApplying, setErrorApplying] = useState(false);

    const handleApply = () => {
        let newFilter = null;
        if (operation && filterValue) {
            if (arrayFilters.includes(column.id) || (column.id === 'department')) {
                newFilter = [];
                for (const singleValue of filterValue) {
                    newFilter.push(
                        { eq: singleValue }
                    );
                }
            } else {
                newFilter = {
                    [operation]: filterValue
                };
                if (column.dataType === 'text' && column.id !== 'employeeId') {
                    newFilter.isCaseSensitive = isCaseSensitive;
                }
            }
            
            // Use the correct filter field name
            const filterFieldName = column.id === 'department' ? 'departmentId' : column.id;
            
            if (filterModel) {
                handleApplyFilter({
                    ...filterModel,
                    [filterFieldName]: newFilter
                });
            } else {
                handleApplyFilter({
                    [filterFieldName]: newFilter
                });
            }
        } else {
            setErrorApplying(true);
        }
    };

    const handleClear = () => {
        if (initialFilterData) {
            // Use the correct filter field name
            const filterFieldName = column.id === 'department' ? 'departmentId' : column.id;
            
            handleApplyFilter({
                ...filterModel,
                [filterFieldName]: null
            });
        }
    };

    const handleClearAll = () => {
        if (filterModel) {
            handleApplyFilter(null);
        }
    };

    const renderFilterControls = () => {
        switch (column?.dataType) {
            case 'text':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Condition</label>
                            <Select 
                                value={operation}
                                onChange={setOperation}
                                style={{ width: '100%' }}
                                size="small"
                                getPopupContainer={() => menuRef.current}
                                allowClear
                            >
                                <Select.Option value="eq">Equal To</Select.Option>
                                <Select.Option value="ne">Not Equal To</Select.Option>
                                <Select.Option value="contains">Contains</Select.Option>
                                <Select.Option value="notContains">Does Not Contain</Select.Option>
                                <Select.Option value="beginsWith">Begins With</Select.Option>
                            </Select>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Value</label>
                            <Input 
                                value={filterValue}
                                onChange={e => setFilterValue(e.target.value)}
                                placeholder="Filter value"
                                allowClear
                                size="small"
                                disabled={!operation}
                            />
                        </div>
                        {column?.id !== 'employeeId' && (
                            <div>
                                <Checkbox 
                                    checked={isCaseSensitive}
                                    onChange={e => setIsCaseSensitive(e.target.checked)}
                                />
                                <span style={{ fontSize: '12px', marginLeft: '4px', marginBottom: '4px' }}>Is Case Sensitive</span>
                            </div>
                        )}
                    </div>
                );
            case 'boolean':
                return (
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Status</label>
                        <Select 
                            value={filterValue}
                            onChange={setFilterValue}
                            placeholder="Select status"
                            allowClear
                            style={{ width: '100%' }}
                            size="small"
                            getPopupContainer={() => menuRef.current}
                        >
                            <Select.Option value={true}>Active</Select.Option>
                            <Select.Option value={false}>Inactive</Select.Option>
                        </Select>
                    </div>
                );
            case 'multiSelect':
                return (
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
                            {arrayLabelsMap[column.id]}
                        </label>
                        <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder="Select an Option"
                            defaultValue={filterValue}
                            onChange={setFilterValue}
                            allowClear
                            options={multiSelectOptions}
                            getPopupContainer={() => menuRef.current}
                        />
                    </div>
                );
            case 'date':
                return (
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
                            Condition
                        </label>
                        <Select 
                            value={operation}
                            onChange={(value) => {
                                setOperation(value);
                                if (value !== 'between') {
                                    setDateRange(null);
                                } else {
                                    setSingleDate(null);
                                }
                            }}
                            style={{ width: '100%' }}
                            size="small"
                            allowClear
                            getPopupContainer={() => menuRef.current}
                        >
                            <Select.Option value="le">Before</Select.Option>
                            <Select.Option value="ge">After</Select.Option>
                            <Select.Option value="between">Between</Select.Option>
                        </Select>
                        {operation === 'between' ? (
                            <>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
                                    Date Range
                                </label>
                                <DatePicker.RangePicker 
                                    value={dateRange}
                                    onChange={(dates) => {
                                        setDateRange(dates);
                                        setFilterValue(dates
                                            ? [toISO(dates[0]), toISO(dates[1])]
                                            : null
                                        );
                                    }}
                                    allowClear
                                    style={{ width: '100%' }}
                                    size="small"
                                    getPopupContainer={() => menuRef.current}
                                />
                            </>
                        ) : (
                            <>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
                                    Date
                                </label>
                                <DatePicker 
                                    value={singleDate}
                                    onChange={(date) => {
                                        setSingleDate(date);
                                        setFilterValue(date ? toISO(date) : null);
                                    }}
                                    style={{ width: '100%' }}
                                    size="small"
                                    allowClear
                                    disabled={!operation}
                                    getPopupContainer={() => menuRef.current}
                                />
                            </>
                        )}
                    </div>
                );
            default:
                return <p>No filter available for this column type.</p>;
        }
    };

    return (
        <div ref={menuRef} style={{ minWidth: '220px' }}>
            <div style={{ marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '900' }}>Filter: {column?.title}</h4>
            </div>
            {renderFilterControls()}
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-around' }}>
                <Button
                    size="small"
                    onClick={handleClear}
                    style={{ marginRight: '8px' }}
                >
                    Clear
                </Button>
                <Button 
                    size="small" 
                    onClick={handleClearAll}
                    style={{ marginRight: '8px' }}
                >
                    Clear All
                </Button>
                <Button 
                    size="small" 
                    onClick={handleCancelFilter} 
                    style={{ marginRight: '8px' }}
                >
                    Cancel
                </Button>
                <Button 
                    type="primary" 
                    size="small" 
                    onClick={handleApply}
                >
                    Apply
                </Button>
            </div>
            {errorApplying && (
                <div style={{ marginTop: '8px', color: 'red' }}>
                    Please enter a valid filter value
                </div>
            )}
        </div>
    );
};

const parseLocation = (raw) => {
    if (!raw) return undefined;
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw);
        } catch {
            return { address: raw };
        }
    }
    return raw;
};
  
const formatLocation = (loc) => {
    if (!loc) return '';
    if (loc.isRemote) {
        const parts = [loc.city, loc.state, loc.country]
            .filter(Boolean)
            .join(', ');
        return parts ? `Remote · ${parts}` : 'Remote';
    }
    if (loc.address) return loc.address;
    const parts = [loc.city, loc.state, loc.country]
        .filter(Boolean)
        .join(', ');
    return parts || '';
};

const QUERY_EMPLOYEES = gql`
  query QueryEmployees(
    $companyId: ID!
    $limit: Int
    $nextToken: String
    $sort: EmployeeSortInput
    $filter: EmployeeFilterInput
  ) {
    queryEmployees(
      companyId: $companyId
      limit: $limit
      nextToken: $nextToken
      sort: $sort
      filter: $filter
    ) {
      items {
        cognitoId
        active
        emailAddress
        firstName
        id
        lastName
        role
        title
        userGroupId
        dateCreated
        departmentId
        employeeId
        additionalInfo {
          careerProfile
          languageCode
          location
          subCompanyId
          recognitionEvents {
            additionalBudgetTotal
            amountPending
            currentBudget
            # ... (rest of fields)
          }
          avatar {
            key
            bucket
            region
          }
        }
      }
      nextToken
      totalCount
    }
  }
`;

const QUERY_DEPARTMENTS = gql`
  query QueryDepartmentsByCompanyIdIndex(
    $companyId: ID!
    $first: Int
    $after: String
  ) {
    queryDepartmentsByCompanyIdIndex(
      companyId: $companyId
      first: $first
      after: $after
    ) {
      items {
      id
      name
      companyId
      keywords
      externalDepartmentId
      active
    }
    nextToken
    }
  }
`;

const QUERY_USERGROUPS = gql`
  query QueryUserGroupsByCompanyIdIndex(
    $companyId: ID!
    $first: Int
    $after: String
  ) {
    queryUserGroupsByCompanyIdIndex(
      companyId: $companyId
      first: $first
      after: $after
    ) {
      items {
      	id
	    active
	    companyId
	    currency
	    keywords
	    measurement
	    name
	    languageCode
      }
      nextToken
    }
  }
`;

const AnimatedExportButton = ({ onExportClick, exportLoading, percentageLoaded, disabled }) => {
    const easeOutBounce = (t) => {
        const n1 = 7.5625;
        const d1 = 2.75;
        
        if (t < 1 / d1) {
            return n1 * t * t;
        } else if (t < 2 / d1) {
            return n1 * (t -= 1.5 / d1) * t + 0.75;
        } else if (t < 2.5 / d1) {
            return n1 * (t -= 2.25 / d1) * t + 0.9375;
        } else {
            return n1 * (t -= 2.625 / d1) * t + 0.984375;
        }
    };
  
    return (
        <AnimatePresence mode="wait">
            {percentageLoaded === 0 && !exportLoading ? (
                <motion.div
                    key="export-button"
                    initial={{ width: 'auto', scale: 1 }}
                    exit={{ 
                        width: 0, 
                        opacity: 0,
                        scale: 0.8,
                        transition: { 
                            width: { duration: 0.2 },
                            opacity: { duration: 0.2 },
                            scale: { duration: 0.3 }
                        }
                    }}
                >
                    <Button onClick={onExportClick} disabled={disabled}>
                        Export CSV
                    </Button>
                </motion.div>
            ) : (
                <motion.div
                    key="progress-bar"
                    initial={{ width: 120, opacity: 0, scale: 0.9 }}
                    animate={{ 
                        width: 300, 
                        opacity: 1,
                        scale: 1,
                        transition: { 
                            width: { duration: 0.8, ease: easeOutBounce },
                            opacity: { duration: 0.3 },
                            scale: { duration: 0.7, ease: easeOutBounce }
                        }
                    }}
                    exit={{ opacity: 0 }}
                >
                    <Progress
                        style={{ width: '100%' }}
                        strokeColor={{
                            from: '#108ee9',
                            to: '#87d068',
                        }}
                        percent={percentageLoaded}
                        status={exportLoading
                            ? 'active'
                            : percentageLoaded === 100
                                ? 'success'
                                : 'exception'
                        }
                    />
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const RoleBadgeRenderer = {
    kind: "role-badge-cell",
    isMatch: (cell) =>
        (cell).data?.kind === "role-badge-cell",
    measure: (ctx, cell) => {
        ctx.font = "12px Inter, sans-serif";
        return ctx.measureText(cell.data?.toUpperCase()).width + 18;
    },
    draw: (args, cell) => {
        const { ctx, theme, rect } = args;
        const { role } = cell.data;
        const bg   = ROLE_COLOURS[role] ?? theme.accentColor;
  
        const r = 6;
        ctx.fillStyle = bg;
        ctx.beginPath();
        ctx.roundRect(rect.x + 4, rect.y + 6, rect.width - 8, rect.height - 12, r);
        ctx.fill();
    
        ctx.font = "12px Inter, sans-serif";
        ctx.fillStyle = "#fff";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(
            role.toUpperCase(),
            rect.x + rect.width / 2,
            rect.y + rect.height / 2 + getMiddleCenterBias(ctx, ctx.font)
        );
  
        return true;
    },
};

const EmployeeGrid = ({
    companyId = '30abdd5d-5e53-4016-9495-7b7ec1444665',
    client,
    currentUser,
    menuColor = "#4285F4",
    theme = {}
}) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortModel, setSortModel] = useState(null);
    const [filterModel, setFilterModel] = useState(null);
    const [nextTokenWithPage, setNextTokenWithPage] = useState({ nextToken: null, page: 0 });
    const [lastNextToken, setLastNextToken] = useState(null);
    const [gridSelection, setGridSelection] = useState();
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [userGroups, setUserGroups] = useState([]);
    const [departments, setDepartments] = useState([]);
    /*
    Example of filterModel:
    {
        firstName: {
            eq: "John",
            isCaseSensitive: true
        },
        lastName: {
            contains: "smith",
            isCaseSensitive: false
        },
        emailAddress: {
            contains: "gmail",
            isCaseSensitive: false
        },
        employeeId: {
            ne: "1234567890"
        },
        departmentId: [
            { eq: "1234567890" },
            { eq: "1234567891" }
        ],
        userGroupId: [
            { eq: "1234567890" },
            { eq: "1234567891" }
        ],
        role: [
            { eq: "admin" },
            { eq: "manager" },
            { eq: "employee" }
        ],
        title: {
            contains: "engineer",
            isCaseSensitive: false
        },
        active: {
            eq: true
        },
        dateCreated: {
            between: ["2024-01-01T00:00:00.000Z", "2024-12-31T23:59:59.999Z"]
        }
    }
    */
    const [pageSize] = useState(50);
    const [currentPage, setCurrentPage] = useState(0);
    const roleOptions = [
        { label: "Admin", value: "admin" },
        { label: "Manager", value: "manager" },
        { label: "Employee", value: "employee" },
        { label: "Extended User", value: "extendedUser" },
        { label: "Super Admin", value: "superAdmin" },
        { label: "Support Admin", value: "supportAdmin" },
    ];
    const [percentageLoaded, setPercentageLoaded] = useState(0);

    const hasMoreData = totalRecords > data.length;

    const [menu, setMenu] = useState(undefined);
    const [activeFilterColumn, setActiveFilterColumn] = useState(null);

    const handleCancelFilter = () => {
        if (menu !== undefined) {
            setMenu(undefined);
            setActiveFilterColumn(null);
        }
    };

    const onDragOverCell = (cell) => {
    };

    /* -------------------------------------------------------------------------- */
    /*  Theme Configuration                                                       */
    /* -------------------------------------------------------------------------- */
    const customTheme = useMemo(() => {
        return {
            ...theme,
            accentColor: menuColor,
            accentLight: `${menuColor}33`,
            headerBackground: menuColor,
            headerForeground: "#FEFFF3",
            borderColor: "#E1E2E5",
            cellHorizontalPadding: 12,
            fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        };
    }, [menuColor, theme]);

    const [hoverRow, setHoverRow] = useState(undefined);
    const onItemHovered = useCallback((args) => {
        const [_, row] = args.location;
        setHoverRow(args.kind !== "cell" ? undefined : row);
    }, []);
    const getRowThemeOverride = useCallback(row => {
        if (row !== hoverRow) return undefined;
        return {
            bgCell: "#f7f7f7",
            bgCellMedium: "#f0f0f0"
        };
    }, [hoverRow]);

    /* -------------------------------------------------------------------------- */
    /*  Column Definitions                                                        */
    /* -------------------------------------------------------------------------- */
    const [columns, setColumns] =  useState([
        {
            title: "User",
            id: "avatar",
            width: 50,
            dataType: 'image'
        },
        {
            title: "VIEW/UPDATE",
            id: "viewUpdate",
            width: 120,
            dataType: 'custom'
        },
        {
            title: "First Name",
            id: "firstName",
            width: 120,
            hasMenu: true,
            menuIcon: "dots",
            dataType: 'text',
            sortable: true
        },
        {
            title: "Last Name",
            id: "lastName",
            width: 120,
            hasMenu: true,
            menuIcon: "dots",
            dataType: 'text',
            sortable: true
        },
        {
            title: "Email",
            id: "emailAddress",
            width: 200,
            hasMenu: true,
            menuIcon: "dots",
            dataType: 'text',
            sortable: true
        },
        {
            title: "Role",
            id: "role",
            width: 120,
            hasMenu: true,
            menuIcon: "dots",
            dataType: 'multiSelect',
            sortable: true
        },
        {
            title: "User Group",
            id: "userGroup",
            width: 150,
            hasMenu: true,
            menuIcon: "dots",
            dataType: 'text',
            sortable: true
        },
        {
            title: "Department",
            id: "department",  // Changed back to "department" for display purposes
            width: 150,
            hasMenu: true,
            menuIcon: "dots",
            dataType: 'multiSelect',  // Keep multiSelect to match the filter type
            sortable: true
        },
        {
            title: "Job Title",
            id: "title",
            width: 150,
            hasMenu: true,
            menuIcon: "dots",
            dataType: 'text',
            sortable: true
        },
        {
            title: "Status",
            id: "active",
            width: 100,
            hasMenu: true,
            menuIcon: "dots",
            dataType: 'boolean',
            sortable: true
        },
        {
            title: "Date Created",
            id: "dateCreated",
            width: 150,
            hasMenu: true,
            menuIcon: "dots",
            dataType: 'date',
            sortable: true
        },
        {
            title: "Location",
            id: "location",
            width: 150,
            dataType: 'json'
        },
        {
            title: "Employee ID",
            id: "employeeId",
            width: 120,
            hasMenu: true,
            menuIcon: "dots",
            dataType: 'text',
            sortable: true
        },
        {
            title: "Budget",
            id: "budget",
            width: 120,
            dataType: 'number'
        }
    ]);
    /* -------------------------------------------------------------------------- */
    /*  Data Fetching                                                             */
    /* -------------------------------------------------------------------------- */
    const fetchData = useCallback(async ({ page = 0, resetData = true, nextTokenArg = null }) => {
        try {
            setLoading(true);
            const { data: departmentData } = await client.query({
                query: QUERY_DEPARTMENTS,
                variables: {
                    companyId: companyId,
                    first: 1000
                }
            });
            const queryVariables = {
                companyId,
                limit: pageSize
            };
            
            if (nextTokenArg) {
                queryVariables.nextToken = nextTokenArg;
            }
            
            if (sortModel) {
                queryVariables.sort = sortModel;
            }
            
            if (filterModel) {
                queryVariables.filter = filterModel;
            }
    
            const { data: responseData } = await client.query({
                query: QUERY_EMPLOYEES,
                variables: queryVariables,
                fetchPolicy: 'network-only',
            });

    
            const { items, totalCount, nextToken } = responseData.queryEmployees;
    
            const safeDownloadFromS3Signed = async (key, fullSize = false) => {
                try {
                    if (fullSize) {
                        return await downloadFromS3Signed(key, 'erin-avatars');
                    } else {
                        return await downloadFromS3Signed(key.replace('user-pic', 'user-pic-thumb'), 'erin-avatars');
                    }
                } catch {
                    if (fullSize) {
                        return '';
                    } else {
                        return await downloadFromS3Signed(key, 'erin-avatars');     
                    }
                }
            }
    
            const rowPromises = items.map(async (i) => {
                let avatarUrl = '';
                let avatarThumbUrl = '';
                
                if (i.additionalInfo?.avatar?.key) {
                    try {
                        avatarUrl = await safeDownloadFromS3Signed(i.additionalInfo.avatar.key, true);
                        avatarThumbUrl = await safeDownloadFromS3Signed(i.additionalInfo.avatar.key.replace('user-pic', 'user-pic-thumb'), false);
                    } catch (error) {
                        console.error('Error loading avatar:', error);
                    }
                }
                
                const rawLocation = i.additionalInfo?.location;
                const locationObject = parseLocation(rawLocation);
                
                return {
                    ...i,
                    locationObject: locationObject,
                    location: formatLocation(locationObject),
                    budget: "$" + (i.additionalInfo?.recognitionEvents?.currentBudget
                        || currentUser?.company?.recognitionSettings?.baseSnappyBudget
                        || '0'),
                    avatar: avatarUrl,
                    avatarThumbnail: avatarThumbUrl
                };
            });
    
            const resolvedRows = await Promise.all(rowPromises);
    
            if (resetData || page === 0) {
                setData(resolvedRows);
            } else {
                setData(prevData => [...prevData, ...resolvedRows]);
            }
            
            setNextTokenWithPage({
                nextToken: nextToken || null,
                page: page + 1
            });
            
            if (page === 0) {
                setTotalRecords(totalCount || 0);
            }
      
            return resolvedRows;
        } catch (err) {
            console.error('Error fetching employees:', err);
            setData([]);
            setTotalRecords(0);
            return [];
        } finally {
            setLoading(false);
        }
    }, [client, companyId, currentUser, filterModel, sortModel, pageSize]);

    // Fetch usergroups and departments once when component loads
    useEffect(() => {
        const fetchUserGroupsAndDepartments = async () => {
            try {
                // Fetch usergroups
                const { data: userGroupsData } = await client.query({
                    query: QUERY_USERGROUPS,
                    variables: {
                        companyId,
                        first: 100 // Fetch up to 100 usergroups
                    },
                    fetchPolicy: 'network-only'
                });
                
                if (userGroupsData?.queryUserGroupsByCompanyIdIndex?.items) {
                    setUserGroups(userGroupsData.queryUserGroupsByCompanyIdIndex.items);
                }
                
                // Fetch departments
                const { data: departmentsData } = await client.query({
                    query: QUERY_DEPARTMENTS,
                    variables: {
                        companyId,
                        first: 100 // Fetch up to 100 departments
                    },
                    fetchPolicy: 'network-only'
                });
                
                if (departmentsData?.queryDepartmentsByCompanyIdIndex?.items) {
                    setDepartments(departmentsData.queryDepartmentsByCompanyIdIndex.items);
                }
            } catch (error) {
                console.error('Error fetching usergroups or departments:', error);
            }
        };
        
        fetchUserGroupsAndDepartments();
    }, [client, companyId]);

    useEffect(() => {
        fetchData({ page: 0, resetData: true });
    }, [fetchData]);

    useEffect(() => {
        setCurrentPage(0);
        setNextTokenWithPage({ nextToken: null, page: 0 });
        setLastNextToken(null);
    }, [filterModel, sortModel]);
  
    const {
        layerProps,
        renderLayer
    } = useLayer({
        isOpen: menu !== undefined,
        auto: true,
        placement: "bottom-end",
        triggerOffset: 2,
        onOutsideClick: handleCancelFilter,
        trigger: {
            getBounds: () => ({
                left: menu?.bounds.x ?? 0,
                top: menu?.bounds.y ?? 0,
                width: menu?.bounds.width ?? 0,
                height: menu?.bounds.height ?? 0,
                right: (menu?.bounds.x ?? 0) + (menu?.bounds.width ?? 0),
                bottom: (menu?.bounds.y ?? 0) + (menu?.bounds.height ?? 0)
            })
        }
    });

    /* -------------------------------------------------------------------------- */
    /*  Cell Value Getters                                                        */
    /* -------------------------------------------------------------------------- */
    // Handle opening the employee details modal
    const handleOpenModal = useCallback((employeeId) => {
        if (!employeeId) {
            console.error('No employee ID provided to handleOpenModal');
            return;
        }
        setSelectedEmployeeId(employeeId);
        setModalVisible(true);
    }, []);

    // Handle closing the employee details modal
    const handleCloseModal = useCallback(() => {
        setModalVisible(false);
        setSelectedEmployeeId(null);
    }, []);

    const getCellContent = useCallback((cell) => {
        const [col, row] = cell;
        const rowData = data[row];
        const column = columns[col];
        const columnId = column.id;
        const cellValue = rowData?.[columnId];
    
        if (!rowData) {
            return { kind: GridCellKind.Loading, allowOverlay: false };
        }
        if (!column) {
            return { kind: GridCellKind.Text, data: "Error", displayData: "Error", readonly: true, allowOverlay: false };
        }
        let value;

        // Handle VIEW/UPDATE button cell
        if (columnId === 'viewUpdate') {
            return createViewUpdateButtonCell(rowData.id, handleOpenModal);
        }
        const possibleTags = [{
            tag: "admin",
            color: "#FAFFD8"
        },
        {
            tag: "manager",
            color: "#F9DCDB"
        },
        {
            tag: "employee",
            color: "#DAE1FD"
        },
        {
            tag: "extendedUser",
            color: "#DDFDFF"
        },
        {
            tag: "superAdmin",
            color: "#E1FEDF"
        },
        {
            tag: "supportAdmin",
            color: "#F8D2F9"
        }];

        switch (columnId) {
            case 'active':
                value = cellValue ? 'Active' : 'Inactive';
                break;
            case 'dateCreated':
                value = dayjs(cellValue).format('MM/DD/YYYY');
                break;
            case 'location':
                const text = rowData.location;
                const isRemote = rowData.locationObject?.isRemote;
                value = isRemote ? `🌐 ${text}` : text;
                break;
            case 'role':
                return {
                    kind: GridCellKind.Custom,
                    allowOverlay: true,
                    readonly: true,
                    data: {
                        kind: "tags-cell",
                        possibleTags,
                        tags: [possibleTags.find(t => t.tag === rowData.role).tag],
                    },
                };
            case 'budget':
                const recognitionEvents = rowData?.additionalInfo?.recognitionEvents;
                let employeeBudget = recognitionEvents?.currentBudget ?? currentUser?.company?.recognitionSettings?.baseSnappyBudget ?? 0;
                if (recognitionEvents?.additionalBudgetTotal > 0) {
                    employeeBudget += recognitionEvents?.additionalBudgetTotal;
                }
                if (recognitionEvents?.amountPending > 0) {
                    employeeBudget -= recognitionEvents?.amountPending;
                }
                value = "$" + (employeeBudget || '0');
                break;
            case 'avatar':
                value = rowData?.avatar;
                break;
            case 'userGroup':
                // Find the user group name based on the userGroupId
                const userGroupId = rowData?.userGroupId;
                const userGroup = userGroups.find(group => group.id === userGroupId);
                value = userGroup ? userGroup.name : 'Not Assigned';
                break;
            case 'department':
            case 'departmentId':
                // Find the department name based on the departmentId
                const departmentId = rowData?.departmentId;
                const department = departments.find(dept => dept.id === departmentId);
                value = department ? department.name : 'Not Assigned';
                break;
            default:
                value = cellValue;
        }
        
        if (columnId === 'avatar' && value) {
            return {
                kind: GridCellKind.Image,
                allowOverlay: true,
                readonly: true,
                displayData: [rowData?.avatarThumbUrl || "", value || ""],
                data: [value || "", rowData?.avatarThumbUrl || ""],
                contentAlign: "center",
                rounding: 100
            };
        }
    
        if (columnId === 'avatar' && !value) {
            return {
                kind: GridCellKind.Custom,
                allowOverlay: true,
                readonly: false,
                data: {
                    kind: "user-profile-cell",
                    image: "",
                    initial: rowData?.firstName ? (rowData?.firstName[0] + rowData?.lastName[0])?.toUpperCase() : "",
                    tint: nameToColor(rowData?.firstName + " " + rowData?.lastName),
                    name: ""
                }
            };
        }

        return {
            kind: GridCellKind.Text,
            allowOverlay: true,
            readonly: true,
            data: value,
            displayData: String(value),
        };
    }, [data, columns, userGroups, departments]);

    /* -------------------------------------------------------------------------- */
    /*  Event Handlers                                                            */
    /* -------------------------------------------------------------------------- */
    const onColumnResize = useCallback((column, newSize) => {
        setColumns((prevColumnsMap) => {
            const index = columns.findIndex(col => col.title === column.title);
            const newArray = [...prevColumnsMap];
            newArray.splice(index, 1, {
                ...prevColumnsMap[index],
                width: newSize
            });
            return newArray;
        });
    }, []);

    const onColumnMoved = useCallback((startIndex, endIndex) => {
        setColumns(prevColumns => {
            const newColumns = [...prevColumns];
            const [toMove] = newColumns.splice(startIndex, 1);
            newColumns.splice(endIndex, 0, toMove);
            return newColumns;
        });
    }, []);

    const onColProposeMove = useCallback((_startIndex, endIndex) => {
        return endIndex !== 3;
    }, []);

    const onHeaderMenuClick = useCallback((colIndex, bounds) => {
        const column = columns[colIndex];
        if (column && column.hasMenu) {
            setActiveFilterColumn(column);
            setMenu({
                col: colIndex,
                bounds
            });
        }
    }, []);

    const onHeaderClicked = useCallback((colIndex, ev) => {
        const isDoubleClick = ev.isDoubleClick;
        const column = columns[colIndex];
        if (isDoubleClick && column.sortable) {
            const currentSortModel = sortModel;
            let newSortModel;
            if (currentSortModel?.field === column.id) {
                if (currentSortModel.direction === 'ASC') {
                    newSortModel = {
                        field: column.id,
                        direction: 'DESC'
                    }
                } else if (currentSortModel.direction === 'DESC') {
                    newSortModel = null;
                }
            } else {
                newSortModel = {
                    field: column.id,
                    direction: 'ASC'
                }
            }
            setSortModel(newSortModel);
        }
    }, [columns, sortModel]);

    const onVisibleRegionChanged = useCallback((region) => {
        const lastVisibleRow = region.y + region.height;
        const shouldLoadMore = 
            hasMoreData && 
            !loading && 
            data.length > 0 && 
            lastVisibleRow >= data.length - 10 && 
            nextTokenWithPage?.nextToken !== null;
        
        if (shouldLoadMore) {
            if (nextTokenWithPage?.nextToken !== lastNextToken) {
                setLastNextToken(nextTokenWithPage?.nextToken);
                fetchData({ 
                    page: currentPage + 1, 
                    resetData: false, 
                    nextTokenArg: nextTokenWithPage?.nextToken 
                });
                setCurrentPage(currentPage + 1);
            }
        }
    }, [data.length, fetchData, currentPage, hasMoreData, loading, nextTokenWithPage, lastNextToken]);

    const handleApplyFilter = useCallback((filterObject) => {
        setFilterModel(filterObject);
        setMenu(undefined);
        setActiveFilterColumn(null);
    }, []);

    /* ------------ */
    /*  CSV Export  */
    /* ------------ */
    const onExportClick = useCallback(async () => {
        try {
            setExportLoading(true);
            setPercentageLoaded(0);
            
            const exportPageSize = 100;
            const BATCH_SIZE = 8;
            let allRowsMap = new Map();
            
            const generateOffsetToken = (offset) => {
                return btoa(JSON.stringify({ offset }));
            };
            
            const initialQueryVariables = {
                companyId,
                limit: 5,
            };
            
            if (sortModel) {
                initialQueryVariables.sort = sortModel;
            }
            
            if (filterModel) {
                initialQueryVariables.filter = filterModel;
            }
            
            const initialResponse = await client.query({
                query: QUERY_EMPLOYEES,
                variables: initialQueryVariables,
                fetchPolicy: 'network-only',
            });
            
            const totalRecordsToExport = initialResponse.data.queryEmployees.totalCount;
            setTotalRecords(totalRecordsToExport);
            
            if (totalRecordsToExport === 0) {
                setExportLoading(false);
                return;
            }
            
            const totalPages = Math.ceil(totalRecordsToExport / exportPageSize);
            
            for (let startPage = 0; startPage < totalPages; startPage += BATCH_SIZE) {
                const batchPromises = [];
                const batchPageIndices = [];
                
                const endPage = Math.min(startPage + BATCH_SIZE, totalPages);
                
                for (let page = startPage; page < endPage; page++) {
                    const offset = page * exportPageSize;
                    
                    const queryVariables = {
                        companyId,
                        limit: exportPageSize,
                        nextToken: generateOffsetToken(offset),
                    };
                    
                    if (sortModel) {
                        queryVariables.sort = sortModel;
                    }
                    
                    if (filterModel) {
                        queryVariables.filter = filterModel;
                    }
                    
                    batchPromises.push(
                        client.query({
                            query: QUERY_EMPLOYEES,
                            variables: queryVariables,
                            fetchPolicy: 'network-only',
                        })
                    );
                    
                    batchPageIndices.push(page);
                }
                
                const batchResults = await Promise.all(batchPromises);
                
                for (let i = 0; i < batchResults.length; i++) {
                    const pageIndex = batchPageIndices[i];
                    const response = batchResults[i];
                    
                    const items = response.data.queryEmployees.items;
                    
                    const rows = items.map(item => ({
                        ...item,
                        location: item.additionalInfo?.location || '',
                        budget: "$" + (item.additionalInfo?.recognitionEvents?.currentBudget
                            || currentUser?.company?.recognitionSettings?.baseSnappyBudget
                            || '0'),
                    }));
                    
                    allRowsMap.set(pageIndex, rows);
                }
                
                const processedPages = Math.min(endPage, totalPages);
                const processedRecords = Math.min(processedPages * exportPageSize, totalRecordsToExport);
                const progress = Math.min(Math.round((processedRecords / totalRecordsToExport) * 100), 99);
                
                setPercentageLoaded(progress);
                
                await new Promise(resolve => setTimeout(resolve, 30));
            }
            
            const allRows = Array.from(allRowsMap)
                .sort(([pageA], [pageB]) => pageA - pageB)
                .flatMap(([_, rows]) => rows);
            
            const headers = columns.map(col => col.title).join(',');
            const csvContent = allRows.map(row => {
                return columns.map(col => {
                    let value;
                    switch (col.id) {
                        case 'active':
                            value = row.active ? 'Active' : 'Inactive';
                            break;
                        case 'dateCreated':
                            value = row.dateCreated ? new Date(row.dateCreated).toLocaleDateString() : '';
                            break;
                        case 'location':
                            value = row.additionalInfo?.location || '';
                            break;
                        case 'budget':
                            const budgetValue = row.additionalInfo?.recognitionEvents?.currentBudget;
                            value = budgetValue !== null && budgetValue !== undefined
                                ? "$" + budgetValue
                                : currentUser?.company?.recognitionSettings?.baseSnappyBudget
                                    ? "$" + currentUser?.company?.recognitionSettings?.baseSnappyBudget
                                    : "$0";
                            break;
                        case 'userGroup':
                            const userGroupId = row.userGroupId;
                            const userGroup = userGroups.find(group => group.id === userGroupId);
                            value = userGroup ? userGroup.name : 'Not Assigned';
                            break;
                        case 'department':
                        case 'departmentId':
                            const departmentId = row.departmentId;
                            const department = departments.find(dept => dept.id === departmentId);
                            value = department ? department.name : 'Not Assigned';
                            break;
                        default:
                            value = row[col.id] || '';
                    }
                    return `"${String(value).replace(/"/g, '""')}"`;
                }).join(',');
            }).join('\n');
            
            const finalCsv = `${headers}\n${csvContent}`;
            
            setPercentageLoaded(100);
            
            const blob = new Blob([finalCsv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `employees_export_${filterModel ? "filtered" : "full"}_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            
            await new Promise(resolve => setTimeout(resolve, 300));
            
            link.click();
            document.body.removeChild(link);
            
            setData(allRows);
            
        } catch (error) {
            console.error('CSV export failed:', error);
            alert('Export failed. Please try again.');
        } finally {
            setExportLoading(false);
        }
    }, [client, companyId, columns, filterModel, sortModel, currentUser, userGroups, departments]);


    /* -------------------------------------------------------------------------- */
    /*  Render                                                                    */
    /* -------------------------------------------------------------------------- */
    return (
        <div>
            <div
                style={{
                    marginBottom: 10,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
                    <span style={{ marginRight: 12, fontSize: 14, fontWeight: 800, color: '#212121' }}>
                        {totalRecords !== undefined ? `${totalRecords} records` : 'Loading records…'}
                    </span>
                    <AnimatedExportButton
                        onExportClick={onExportClick}
                        exportLoading={exportLoading}
                        percentageLoaded={percentageLoaded}
                        disabled={exportLoading || data?.length === 0}
                    />
                </div>
            </div>
            <div>
                <DataEditor
                    columns={columns}
                    columnSelectionBlending="mixed"
                    customRenderers={[UserProfileCell, RoleBadgeRenderer, TagsCell, ViewUpdateButtonRenderer]}
                    //freezeColumns={1} // Columns will be sticky if > 0
                    getCellContent={getCellContent}
                    getCellsForSelection={true}
                    getRowThemeOverride={getRowThemeOverride}
                    headerHeight={40}
                    keybindings={{search: true}}
                    maxColumnWidth={2000}
                    onCellContextMenu={(_, e) => e.preventDefault()}
                    onColumnMoved={onColumnMoved}
                    onColProposeMove={onColProposeMove}
                    onColumnResize={onColumnResize}
                    onDragOverCell={onDragOverCell}
                    onHeaderClicked={onHeaderClicked}
                    onHeaderMenuClick={onHeaderMenuClick}
                    onItemHovered={onItemHovered}
                    onVisibleRegionChanged={onVisibleRegionChanged}
                    rangeSelectionBlending="mixed"
                    rowHeight={35}
                    height={"800px"}
                    rowMarkers="number"
                    rows={data.length} // This is row length, not row data
                    smoothScrollX={true}
                    smoothScrollY={true}
                    theme={customTheme}
                    verticalBorder={true}
                    onCellClicked={(cell, e) => {
                        const [col, row] = cell;
                        const column = columns[col];
                        if (column.id === 'viewUpdate') {
                            const rowData = data[row];
                            handleOpenModal(rowData.id);
                            return true;
                        }
                        return false;
                    }}
                />
            </div>
            {renderLayer(
                <AnimatePresence>
                    {menu !== undefined && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.85 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.85 }}
                            transition={{ duration: 0.15 }}
                            key="filter-menu"
                            {...layerProps}
                            style={{
                                ...layerProps.style,
                                padding: "16px",
                                borderRadius: "10px",
                                backgroundColor: "#FFFFFF",
                                boxShadow: '0 0 1px #3e4156b3, 0 6px 12px #3e415659',
                                color: "#212121",
                                maxWidth: "400px", // Ensure popup isn't too wide
                            }}
                        >
                            {activeFilterColumn && (
                                <FilterMenu
                                    column={activeFilterColumn}
                                    filterModel={filterModel}
                                    initialFilterData={
                                        activeFilterColumn.id === 'department'
                                            ? filterModel?.departmentId || null
                                            : filterModel?.[activeFilterColumn.id] || null
                                    }
                                    handleApplyFilter={handleApplyFilter}
                                    handleCancelFilter={handleCancelFilter}
                                    multiSelectOptions={
                                        activeFilterColumn.id === 'role' ? roleOptions :
                                        activeFilterColumn.id === 'userGroupId' ? userGroups.map(group => ({
                                            label: group.name,
                                            value: group.id
                                        })) :
                                        activeFilterColumn.id === 'departmentId' || activeFilterColumn.id === 'department' ? departments.map(dept => ({
                                            label: dept.name,
                                            value: dept.id
                                        })) :
                                        null
                                    }
                                />
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
            <div id="portal" />
            
            {/* Employee Details Modal */}
            {selectedEmployeeId && (
                <EmployeeDetailsModalContainer
                    visible={modalVisible}
                    employeeId={selectedEmployeeId}
                    onCancel={handleCloseModal}
                    client={client}
                    currentUser={currentUser}
                    departments={departments}
                    userGroups={userGroups}
                />
            )}
        </div>
    );
};

export default EmployeeGrid;
